import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const FALLBACK = {
  chip: "Morning Soft® · Everyday Comfort",
  title: "Comfort for every part of your day.",
  subtitle: "From bathroom tissue and kitchen towels to facial tissue and wipes, Morning Soft® delivers softness, strength, and dependable quality for every part of your day.",
  cta1_label: "Shop Products",
  cta1_href: "/shop",
  cta2_label: "Discover Morning Soft",
  cta2_href: "/about",
  image: "/products/hero_family.png",
};

/**
 * Hero — product-forward split layout.
 * Left 42%: brand + headline + copy + CTAs.
 * Right 58%: brand family image.
 */
export const Hero = () => {
  const { content } = useContent();
  const h = { ...FALLBACK, ...(content?.hero || {}) };
  const stagger = { transition: { staggerChildren: 0.08, delayChildren: 0.15 } };
  const rise = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-gradient-to-b from-sky-100 via-sky-50 to-white"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <motion.div
          animate={{ y: [0, 50, 0], x: [0, 30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-sky-200/70 blur-3xl"
        />
        <motion.div
          animate={{ y: [0, -40, 0], x: [0, -30, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
          className="absolute top-1/3 -right-32 w-[560px] h-[560px] rounded-full bg-sky-300/50 blur-3xl"
        />
        <motion.div
          animate={{ y: [0, 30, 0], x: [0, 25, 0], scale: [1, 1.08, 1] }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
          className="absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full bg-amber-200/50 blur-3xl"
        />
        {/* Floating sparkles */}
        {[
          { top: "18%", left: "48%", size: 8, delay: 0 },
          { top: "72%", left: "22%", size: 10, delay: 1.2 },
          { top: "40%", left: "8%", size: 6, delay: 2.4 },
          { top: "12%", left: "38%", size: 5, delay: 0.6 },
          { top: "58%", left: "68%", size: 7, delay: 1.8 },
          { top: "28%", left: "82%", size: 9, delay: 3.0 },
          { top: "82%", left: "52%", size: 6, delay: 2.2 },
        ].map((s, i) => (
          <motion.span
            key={i}
            className="absolute rounded-full bg-white shadow-[0_0_24px_rgba(255,255,255,1)]"
            style={{ top: s.top, left: s.left, width: s.size, height: s.size }}
            animate={{ y: [0, -30, 0], opacity: [0.3, 1, 0.3], scale: [0.8, 1.3, 0.8] }}
            transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: "easeInOut", delay: s.delay }}
          />
        ))}
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-12 pb-16 md:pt-20 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center min-h-[calc(100vh-6rem)]">
        {/* LEFT — text block */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="lg:col-span-5 relative z-10"
        >
          <motion.div variants={rise} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/70 border border-sky-100 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#173a7a]">
              {h.chip}
            </span>
          </motion.div>

          <motion.h1
            variants={rise}
            data-testid="hero-title"
            className="mt-6 font-display font-black tracking-tight text-[#0f2a5c] text-4xl sm:text-5xl md:text-6xl leading-[1.02]"
          >
            {h.title}
          </motion.h1>

          <motion.p
            variants={rise}
            data-testid="hero-subtitle"
            className="mt-6 max-w-lg text-base md:text-lg text-slate-600 leading-relaxed"
          >
            {h.subtitle}
          </motion.p>

          <motion.div variants={rise} className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              to={h.cta1_href}
              data-testid="hero-shop-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#173a7a] text-white font-display font-bold px-7 py-4 text-sm uppercase tracking-widest hover:bg-[#1a4694] transition-colors"
            >
              {h.cta1_label} <ArrowRight size={16} />
            </Link>
            <Link
              to={h.cta2_href}
              data-testid="hero-discover-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#173a7a] text-[#173a7a] font-display font-bold px-7 py-4 text-sm uppercase tracking-widest hover:bg-[#173a7a] hover:text-white transition-colors"
            >
              {h.cta2_label}
            </Link>
          </motion.div>

          <motion.div
            variants={rise}
            className="mt-10 flex items-center gap-6 text-xs text-slate-500"
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-8 h-[2px] bg-amber-400" /> Softness you can feel
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-8 h-[2px] bg-[#173a7a]" /> Quality you can trust
            </span>
          </motion.div>
        </motion.div>

        {/* RIGHT — brand family image */}
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="relative"
            data-testid="hero-image"
          >
            {/* Gentle floating wrapper */}
            <motion.div
              animate={{ y: [0, -22, 0], rotate: [0, -0.6, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="relative rounded-[28px] md:rounded-[36px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(23,58,122,0.45)] border border-white/60"
            >
              <img
                src={h.image}
                alt="Morning Soft product family"
                className="w-full h-auto object-cover"
              />
              {/* Soft moving highlight sweep */}
              <motion.div
                aria-hidden
                className="pointer-events-none absolute -inset-y-4 -left-1/2 w-2/3 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12"
                animate={{ left: ["-50%", "150%"] }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 2 }}
              />
            </motion.div>

            {/* Floating trust chip — top right (orbiting motion) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6, y: -10 }}
              animate={{
                opacity: 1,
                scale: 1,
                y: [0, -12, 0],
                x: [0, 8, 0],
                rotate: [-2, 2, -2],
              }}
              transition={{
                opacity: { duration: 0.6, delay: 1 },
                scale: { duration: 0.6, delay: 1 },
                y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
                x: { duration: 5, repeat: Infinity, ease: "easeInOut" },
                rotate: { duration: 6, repeat: Infinity, ease: "easeInOut" },
              }}
              className="absolute -top-4 right-6 md:right-10 hidden md:flex items-center gap-2 rounded-full bg-white border border-amber-100 px-4 py-2 shadow-xl"
            >
              <motion.span
                animate={{ scale: [1, 1.6, 1], opacity: [1, 0.5, 1] }}
                transition={{ duration: 1.4, repeat: Infinity }}
                className="w-2 h-2 rounded-full bg-amber-400"
              />
              <span className="text-xs font-bold text-[#173a7a]">2-Ply · 320 sheets</span>
            </motion.div>

            {/* Floating trust chip — bottom left (orbiting motion) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6, y: 10 }}
              animate={{
                opacity: 1,
                scale: 1,
                y: [0, 14, 0],
                x: [0, -8, 0],
                rotate: [2, -2, 2],
              }}
              transition={{
                opacity: { duration: 0.6, delay: 1.2 },
                scale: { duration: 0.6, delay: 1.2 },
                y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 },
                x: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 },
                rotate: { duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.5 },
              }}
              className="absolute -bottom-4 left-6 md:left-10 hidden md:flex items-center gap-2 rounded-full bg-[#173a7a] px-4 py-2 shadow-xl"
            >
              <motion.span
                animate={{ scale: [1, 1.6, 1] }}
                transition={{ duration: 1.4, repeat: Infinity, delay: 0.7 }}
                className="w-2 h-2 rounded-full bg-amber-400"
              />
              <span className="text-xs font-bold text-white">Made for everyday</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
